// Chat state machine + SSE pipeline plumbing — extracted from App.tsx so every
// shell consumes the same hook. The shape (current, priorTurns, submit,
// askAnother) is the contract every shell receives via ShellProps.
import { useCallback, useEffect, useRef, useState } from 'react';
import { streamQuery } from './api';
import type {
  AssistantTurn,
  InitEventData,
  PresentationEventData,
  StageEventData,
  StreamEvent,
  ToolCallEventData,
  ToolResultEventData,
  Turn,
} from './types';
import type { CurrentTurn } from './shells/types';

const EMPTY_PRESENTATION: AssistantTurn['presentation'] = { type: 'prose' };

export interface UseChatState {
  current: CurrentTurn | null;
  priorTurns: Turn[];
  submit: (query: string) => void;
  askAnother: () => void;
}

export function useChatState(): UseChatState {
  const [current, setCurrent] = useState<CurrentTurn | null>(null);
  const [priorTurns, setPriorTurns] = useState<Turn[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  // Abort any in-flight stream when the component unmounts (HMR, SPA nav, etc.)
  // so the fetch reader stops and pending setState calls aren't issued against
  // a torn-down tree.
  useEffect(() => () => {
    abortRef.current?.abort();
  }, []);

  const askAnother = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (current && current.assistant.prose && !current.streaming) {
      // Promote completed turn into priorTurns so the next ask is a follow-up.
      setPriorTurns((prev) => [
        ...prev,
        { user_query: current.query, assistant_summary: current.assistant.prose },
      ]);
    }
    setCurrent(null);
  }, [current]);

  const submit = useCallback(
    (query: string) => {
      // Stop any in-flight stream before starting a new one.
      abortRef.current?.abort();
      const ctl = new AbortController();
      abortRef.current = ctl;

      setCurrent({
        query,
        assistant: {
          interactionId: '',
          toolsUsed: [],
          prose: '',
          presentation: EMPTY_PRESENTATION,
          citations: [],
        },
        toolsInFlight: [],
        streaming: true,
      });

      streamQuery(
        { query, prior_turns: priorTurns },
        {
          signal: ctl.signal,
          onEvent: (ev: StreamEvent) => {
            setCurrent((prev) => prev ? applyEvent(prev, ev) : prev);
          },
          onError: (err) => {
            if (ctl.signal.aborted) return;
            setCurrent((prev) =>
              prev
                ? {
                    ...prev,
                    streaming: false,
                    assistant: { ...prev.assistant, error: err.message },
                  }
                : prev,
            );
          },
        },
      );
    },
    [priorTurns],
  );

  return { current, priorTurns, submit, askAnother };
}

export function applyEvent(prev: CurrentTurn, ev: StreamEvent): CurrentTurn {
  switch (ev.type) {
    case 'init': {
      const data = ev.data as InitEventData;
      return {
        ...prev,
        assistant: { ...prev.assistant, interactionId: data.interaction_id },
      };
    }
    case 'stage': {
      const data = ev.data as StageEventData;
      return {
        ...prev,
        stageLabel: data.label || prev.stageLabel,
      };
    }
    case 'tool_call': {
      const data = ev.data as ToolCallEventData;
      const alreadySeen = prev.assistant.toolsUsed.includes(data.tool_key);
      return {
        ...prev,
        toolsInFlight: [...prev.toolsInFlight, data.tool_key],
        assistant: {
          ...prev.assistant,
          toolsUsed: alreadySeen
            ? prev.assistant.toolsUsed
            : [...prev.assistant.toolsUsed, data.tool_key],
        },
      };
    }
    case 'tool_result': {
      // Remove the matching tool from in-flight by NAME, not by position —
      // when multiple tools fan out in parallel, completion order can differ
      // from dispatch order. Fall back to slice(1) only when a stub event
      // omits the tool_key (older test fixtures, mostly).
      const data = (ev.data ?? {}) as ToolResultEventData;
      const toolKey = data.tool_key;
      const nextInFlight = toolKey
        ? prev.toolsInFlight.filter((t) => t !== toolKey)
        : prev.toolsInFlight.slice(1);
      // Defensive: backfill toolsUsed in case the matching tool_call event
      // was dropped (network blip, frame split across reads, etc.).
      const alreadySeen = !toolKey || prev.assistant.toolsUsed.includes(toolKey);
      return {
        ...prev,
        toolsInFlight: nextInFlight,
        assistant: {
          ...prev.assistant,
          toolsUsed: alreadySeen
            ? prev.assistant.toolsUsed
            : [...prev.assistant.toolsUsed, toolKey!],
        },
      };
    }
    case 'token': {
      const data = ev.data as { text: string };
      return {
        ...prev,
        assistant: {
          ...prev.assistant,
          prose: prev.assistant.prose + data.text,
        },
      };
    }
    case 'presentation': {
      const data = ev.data as PresentationEventData;
      return {
        ...prev,
        assistant: {
          ...prev.assistant,
          presentation: data.presentation,
          citations: data.citations,
        },
      };
    }
    case 'done': {
      return {
        ...prev,
        streaming: false,
        toolsInFlight: [],
        assistant: { ...prev.assistant, doneAt: Date.now() },
      };
    }
    case 'error': {
      const data = ev.data as { message: string };
      return {
        ...prev,
        streaming: false,
        assistant: { ...prev.assistant, error: data.message },
      };
    }
    default:
      return prev;
  }
}
